import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronDown } from 'lucide-react';

import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

import { partnerApi } from '../lib/api/partnerApi';
import { PartnerDto } from '../lib/api/types';

interface PartnerSelectorProps {
  selectedPartnerId?: string;
  onPartnerSelect: (partner: PartnerDto | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

export const PartnerSelector: React.FC<PartnerSelectorProps> = ({
  selectedPartnerId,
  onPartnerSelect,
  label = 'Partner',
  placeholder = 'Select a partner',
  required = false,
  disabled = false,
  error,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Query to fetch all active partners
  const {
    data: partnerData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['partners'],
    queryFn: partnerApi.getAllPartners,
  });

  const partners = partnerData?.partners?.filter(p => p.isActive) || [];
  const selectedPartner = partners.find(p => p.partnerId === selectedPartnerId);

  const handlePartnerSelect = (partner: PartnerDto | null) => {
    onPartnerSelect(partner);
    setIsOpen(false);
  };

  if (queryError) {
    return (
      <div className="space-y-2">
        {label && (
          <Label className={required ? "after:content-['*'] after:ml-0.5 after:text-destructive" : ""}>
            {label}
          </Label>
        )}
        <div className="text-sm text-destructive">
          Failed to load partners
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && (
        <Label className={required ? "after:content-['*'] after:ml-0.5 after:text-destructive" : ""}>
          {label}
        </Label>
      )}

      <div className="relative">
        <Button
          type="button"
          variant="outline"
          className={`w-full justify-between ${
            error ? 'border-destructive' : ''
          } ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span className={selectedPartner ? '' : 'text-muted-foreground'}>
              {isLoading
                ? 'Loading partners...'
                : selectedPartner
                ? `${selectedPartner.partnerName} (${selectedPartner.partnerCode})`
                : placeholder}
            </span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} />
        </Button>

        {isOpen && !disabled && (
          <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto">
            <CardContent className="p-0">
              {/* Clear selection option */}
              <button
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-muted transition-colors border-b"
                onClick={() => handlePartnerSelect(null)}
              >
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4" /> {/* Spacer */}
                  <span className="text-muted-foreground">No partner selected</span>
                </div>
              </button>

              {/* Partner options */}
              {partners.length === 0 ? (
                <div className="px-3 py-4 text-center text-muted-foreground">
                  No active partners available
                </div>
              ) : (
                partners.map((partner) => (
                  <button
                    key={partner.partnerId}
                    type="button"
                    className={`w-full px-3 py-2 text-left hover:bg-muted transition-colors ${
                      partner.partnerId === selectedPartnerId ? 'bg-muted' : ''
                    }`}
                    onClick={() => handlePartnerSelect(partner)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <div>
                        <div className="font-medium">{partner.partnerName}</div>
                        <div className="text-sm text-muted-foreground">
                          {partner.partnerCode}
                          {partner.description && ` • ${partner.description}`}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
